import React, { useEffect, useState } from 'react';
import { useAppContext } from '../context/AppContext';
import axios from 'axios';
import appConstants from '../constants/appConstants';
import { Modal, Button, Form } from 'react-bootstrap';
import { FaEdit, FaPlus } from 'react-icons/fa';
import Layout from '../components/Layout';
import { useNavigate } from 'react-router-dom';

const getAuthToken = () => localStorage.getItem('authToken');

const TestsPage = () => {
  const { selectedAcademicYear } = useAppContext();
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [examTypes, setExamTypes] = useState([]);
  const [batches, setBatches] = useState([]);
  const [filterType, setFilterType] = useState('');
  const [filterBatch, setFilterBatch] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    subject_id: '',
    exam_date: '',
    type_id: '',
    batch_id: '',
    evaluation_type: 'mark',
    total_mark: '',
  });

  const [compareBatchId, setCompareBatchId] = useState('');
  const [availableTests, setAvailableTests] = useState([]);
  const [selectedTestIds, setSelectedTestIds] = useState([]);
  const [showCompareSection, setShowCompareSection] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);

  useEffect(() => {
    if (compareBatchId) {
      const batchTests = tests.filter((t) => t.batch_id == compareBatchId);
      setAvailableTests(batchTests);
    } else {
      setAvailableTests([]);
      setSelectedTestIds([]);
    }
  }, [compareBatchId, tests]);

  const fetchData = async () => {
    const token = getAuthToken();
    if (!token) return;

    if (!selectedAcademicYear || !selectedAcademicYear.id) {
      console.error('No academic year selected');
      return;
    }

    try {
      const [testRes, subjectRes, typeRes, batchRes] = await Promise.all([
        axios.get(`${appConstants.API_ROOT_URL}get-tests-v2`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { academic_year_id: selectedAcademicYear.id },
        }),
        axios.get(`${appConstants.API_ROOT_URL}get-subjects`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${appConstants.API_ROOT_URL}get-exam-types`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${appConstants.API_ROOT_URL}get-batches-v3`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { academic_year_id: selectedAcademicYear.id },
        }),
      ]);
      setTests(testRes.data.data);
      setSubjects(subjectRes.data.data);
      setExamTypes(typeRes.data.data);
      setBatches(batchRes.data.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [selectedAcademicYear]);

  const openEnterTestMarksPage = (test) => {
    navigate(`/test-mark-entry/${test.id}`);
  };

  const filteredTests = tests.filter((test) => {
    return (
      (!filterType || test.type_id == filterType) &&
      (!filterSubject || test.subject_id == filterSubject) &&
      (!filterBatch || test.batch_id == filterBatch)
    );
  });

  const openModal = (test = null) => {
    if (test) {
      setEditData(test);
      setFormData({ ...test });
    } else {
      setEditData(null);
      setFormData({
        name: '',
        subject_id: '',
        exam_date: '',
        type_id: '',
        batch_id: '',
        evaluation_type: 'mark',
        total_mark: '',
      });
    }
    setShowModal(true);
  };

  const handleDeleteTest = async (testId) => {
    if (
      !window.confirm(
        'Are you sure you want to delete this test and all its marks?',
      )
    )
      return;

    const token = localStorage.getItem('authToken');
    if (!token) {
      alert('Unauthorized! Please log in.');
      return;
    }

    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}delete-test?test_id=${testId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.status === 'success') {
        //alert("Test deleted successfully.");
        fetchData(); // Refresh the list
      } else {
        // Handle API-specific errors
        alert(response.data.message || 'Failed to delete test.');
      }
    } catch (error) {
      // Handle specific types of errors
      if (error.response) {
        // Server responded with a status other than 2xx
        console.error('Error response from server:', error.response);
        alert(
          `Server error: ${
            error.response.data.message ||
            'An error occurred while deleting the test.'
          }`,
        );
      } else if (error.request) {
        // No response received from the server
        console.error('No response from server:', error.request);
        alert('Network error: Please check your internet connection.');
      } else {
        // Something else happened
        console.error('Error setting up the request:', error.message);
        alert(`Unexpected error: ${error.message}`);
      }
    }
  };

  const handleViewTest = (test) => {
    navigate(`/test-marks-report/${test.id}`);
  };

  const handleSubmit = async () => {
    const token = getAuthToken();
    if (!token) {
      alert('Unauthorized! Please log in.');
      return;
    }

    const url = editData
      ? `${appConstants.API_ROOT_URL}edit-test-v2`
      : `${appConstants.API_ROOT_URL}create-test-v2`;

    const payload = {
      ...formData,
      total_mark: formData.total_mark || 0,
    };

    try {
      const response = await axios.post(url, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.status === 'success') {
        fetchData();
        setShowModal(false);
      } else {
        alert(
          response.data.message || 'Something went wrong. Please try again.',
        );
      }
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        alert(`Error: ${error.response.data.message}`);
      } else {
        alert(
          'Unexpected error occurred. Please check your connection and try again.',
        );
      }
      console.error('Error saving test:', error);
    }
  };

  const handleCompareMarks = () => {
    if (!compareBatchId || selectedTestIds.length < 2) {
      alert('Please select a batch and at least two tests to compare.');
      return;
    }

    navigate(
      `/compare-marks?batch=${compareBatchId}&tests=${selectedTestIds.join(
        ',',
      )}`,
    );
  };

  return (
    <Layout>
      {/* Top Bar */}
      <div
        style={{
          background: '#343a40',
          color: '#fff',
          borderRadius: 8,
          padding: '0.7rem 1rem 0.7rem 1rem',
          marginBottom: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
          gap: 15,
        }}
      >
        <div
          style={{
            fontSize: 22,
            fontWeight: 600,
            letterSpacing: 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <FaEdit />
          Tests
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Form.Select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            style={{
              borderRadius: 6,
              border: 'none',
              padding: '6px 12px',
              fontSize: '0.85rem',
              width: 110,
              background: 'white',
            }}
          >
            <option value="">All Types</option>
            {examTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Form.Select>

          <Form.Select
            value={filterBatch}
            onChange={(e) => setFilterBatch(e.target.value)}
            style={{
              borderRadius: 6,
              border: 'none',
              padding: '6px 12px',
              fontSize: '0.85rem',
              width: 110,
              background: 'white',
            }}
          >
            <option value="">All Batches</option>
            {batches.map((t) => (
              <option key={t.id} value={t.id}>
                {t.class_name} {t.division}
              </option>
            ))}
          </Form.Select>

          <Form.Select
            value={filterSubject}
            onChange={(e) => setFilterSubject(e.target.value)}
            style={{
              borderRadius: 6,
              border: 'none',
              padding: '6px 12px',
              fontSize: '0.85rem',
              width: 110,
              background: 'white',
            }}
          >
            <option value="">All Subjects</option>
            {subjects.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Form.Select>

          {(filterType || filterBatch || filterSubject) && (
            <Button
              variant="outline-light"
              size="sm"
              onClick={() => {
                setFilterType('');
                setFilterBatch('');
                setFilterSubject('');
              }}
              style={{
                borderRadius: 6,
                padding: '6px 12px',
                fontSize: '0.8rem',
                fontWeight: 500,
              }}
            >
              Clear
            </Button>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button
            onClick={() => setShowCompareModal(true)}
            variant="outline-light"
            size="sm"
            style={{
              borderRadius: 6,
              padding: '8px 16px',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            📊 Compare
          </Button>
          <Button
            onClick={() => openModal()}
            variant="light"
            size="sm"
            style={{
              borderRadius: 6,
              padding: '8px 16px',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <FaPlus />
            Create Test
          </Button>
        </div>
      </div>

      {/* Tests Table */}
      <div
        className="card"
        style={{
          border: 'none',
          borderRadius: '15px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
          overflow: 'hidden',
        }}
      >
        <div style={{ overflowX: 'auto' }}>
          <table className="table" style={{ marginBottom: 0 }}>
            <thead
              style={{
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
              }}
            >
              <tr>
                <th
                  style={{
                    padding: '18px 20px',
                    fontWeight: '700',
                    color: '#495057',
                    fontSize: '0.85rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '2px solid #dee2e6',
                    width: '30%',
                  }}
                >
                  Test Details
                </th>
                <th
                  style={{
                    padding: '18px 20px',
                    fontWeight: '700',
                    color: '#495057',
                    fontSize: '0.85rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '2px solid #dee2e6',
                  }}
                >
                  Batch & Subject
                </th>
                <th
                  style={{
                    padding: '18px 20px',
                    fontWeight: '700',
                    color: '#495057',
                    fontSize: '0.85rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '2px solid #dee2e6',
                    textAlign: 'center',
                  }}
                >
                  Type & Evaluation
                </th>
                <th
                  style={{
                    padding: '18px 20px',
                    fontWeight: '700',
                    color: '#495057',
                    fontSize: '0.85rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '2px solid #dee2e6',
                    textAlign: 'center',
                    width: '25%',
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredTests.map((test, index) => (
                <tr
                  key={test.id}
                  style={{
                    background: index % 2 === 0 ? '#ffffff' : '#fafbfc',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f0f4ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background =
                      index % 2 === 0 ? '#ffffff' : '#fafbfc';
                  }}
                >
                  <td
                    style={{
                      padding: '16px 20px',
                      borderBottom: '1px solid #f0f0f0',
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontWeight: '700',
                          color: '#2c3e50',
                          fontSize: '1rem',
                          marginBottom: '6px',
                        }}
                      >
                        {test.name}
                      </div>
                      <div
                        style={{
                          fontSize: '0.85rem',
                          color: '#7f8c8d',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <span>📅 {test.exam_date}</span>
                        {test.total_mark > 0 && (
                          <>
                            <span>•</span>
                            <span
                              style={{ fontWeight: '600', color: '#2c3e50' }}
                            >
                              Max: {test.total_mark}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </td>
                  <td
                    style={{
                      padding: '16px 20px',
                      borderBottom: '1px solid #f0f0f0',
                    }}
                  >
                    <div>
                      <span
                        style={{
                          background: '#e8eaf6',
                          padding: '4px 10px',
                          borderRadius: '5px',
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          color: '#3f51b5',
                          display: 'inline-block',
                          marginBottom: '6px',
                        }}
                      >
                        {test.batch_details}
                      </span>
                      <div
                        style={{
                          fontSize: '0.9rem',
                          color: '#5a6c7d',
                        }}
                      >
                        {subjects.find((s) => s.id === test.subject_id)?.name}
                      </div>
                    </div>
                  </td>
                  <td
                    style={{
                      padding: '16px 20px',
                      borderBottom: '1px solid #f0f0f0',
                      textAlign: 'center',
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: '0.85rem',
                          color: '#5a6c7d',
                          marginBottom: '8px',
                          fontWeight: '500',
                        }}
                      >
                        {examTypes.find((t) => t.id === test.type_id)?.name}
                      </div>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '0.3px',
                          background:
                            test.evaluation_type === 'mark'
                              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                              : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                          color: 'white',
                        }}
                      >
                        {test.evaluation_type}
                      </span>
                    </div>
                  </td>
                  <td
                    style={{
                      padding: '12px 20px',
                      borderBottom: '1px solid #f0f0f0',
                    }}
                  >
                    <div
                      className="d-flex gap-1 justify-content-center"
                      style={{ flexWrap: 'nowrap' }}
                    >
                      <Button
                        size="sm"
                        onClick={() => openModal(test)}
                        title="Edit Test"
                        style={{
                          background: 'transparent',
                          border: '1px solid #667eea',
                          color: '#667eea',
                          borderRadius: '6px',
                          padding: '6px 10px',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          minWidth: '70px',
                          transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#667eea';
                          e.currentTarget.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = '#667eea';
                        }}
                      >
                        <FaEdit />
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => openEnterTestMarksPage(test)}
                        title="Enter Marks"
                        style={{
                          background:
                            'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '6px 10px',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          minWidth: '70px',
                          boxShadow: '0 2px 6px rgba(17, 153, 142, 0.3)',
                        }}
                      >
                        Marks
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleViewTest(test)}
                        title="View Report"
                        style={{
                          background: 'transparent',
                          border: '1px solid #f57c00',
                          color: '#f57c00',
                          borderRadius: '6px',
                          padding: '6px 10px',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          minWidth: '70px',
                          transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#f57c00';
                          e.currentTarget.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = '#f57c00';
                        }}
                      >
                        View
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleDeleteTest(test.id)}
                        title="Delete Test"
                        style={{
                          background: 'transparent',
                          border: '1px solid #dc3545',
                          color: '#dc3545',
                          borderRadius: '6px',
                          padding: '6px 10px',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          minWidth: '70px',
                          transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#dc3545';
                          e.currentTarget.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = '#dc3545';
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        size="lg"
      >
        <Modal.Header
          closeButton
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            padding: '20px 25px',
          }}
        >
          <Modal.Title style={{ fontSize: '1.3rem', fontWeight: '600' }}>
            {editData ? (
              <>
                <FaEdit className="me-2" />
                Edit Test
              </>
            ) : (
              <>
                <FaPlus className="me-2" />
                Create New Test
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '30px' }}>
          <Form>
            <div className="row g-3">
              <div className="col-md-12">
                <Form.Group>
                  <Form.Label
                    style={{
                      fontWeight: '600',
                      color: '#333',
                      marginBottom: '8px',
                    }}
                  >
                    Test Name
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="Enter test name"
                    style={{
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0',
                      padding: '12px',
                    }}
                  />
                </Form.Group>
              </div>

              <div className="col-md-6">
                <Form.Group>
                  <Form.Label
                    style={{
                      fontWeight: '600',
                      color: '#333',
                      marginBottom: '8px',
                    }}
                  >
                    Subject
                  </Form.Label>
                  <Form.Select
                    value={formData.subject_id}
                    onChange={(e) =>
                      setFormData({ ...formData, subject_id: e.target.value })
                    }
                    style={{
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0',
                      padding: '12px',
                    }}
                  >
                    <option value="">Select Subject</option>
                    {subjects.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>

              <div className="col-md-6">
                <Form.Group>
                  <Form.Label
                    style={{
                      fontWeight: '600',
                      color: '#333',
                      marginBottom: '8px',
                    }}
                  >
                    Exam Type
                  </Form.Label>
                  <Form.Select
                    value={formData.type_id}
                    onChange={(e) =>
                      setFormData({ ...formData, type_id: e.target.value })
                    }
                    style={{
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0',
                      padding: '12px',
                    }}
                  >
                    <option value="">Select Type</option>
                    {examTypes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>

              <div className="col-md-6">
                <Form.Group>
                  <Form.Label
                    style={{
                      fontWeight: '600',
                      color: '#333',
                      marginBottom: '8px',
                    }}
                  >
                    Batch
                  </Form.Label>
                  <Form.Select
                    value={formData.batch_id}
                    onChange={(e) =>
                      setFormData({ ...formData, batch_id: e.target.value })
                    }
                    style={{
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0',
                      padding: '12px',
                    }}
                  >
                    <option value="">Select Batch</option>
                    {batches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.class_name} {b.division}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>

              <div className="col-md-6">
                <Form.Group>
                  <Form.Label
                    style={{
                      fontWeight: '600',
                      color: '#333',
                      marginBottom: '8px',
                    }}
                  >
                    Exam Date
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.exam_date}
                    onChange={(e) =>
                      setFormData({ ...formData, exam_date: e.target.value })
                    }
                    style={{
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0',
                      padding: '12px',
                    }}
                  />
                </Form.Group>
              </div>

              <div className="col-md-6">
                <Form.Group>
                  <Form.Label
                    style={{
                      fontWeight: '600',
                      color: '#333',
                      marginBottom: '8px',
                    }}
                  >
                    Evaluation Type
                  </Form.Label>
                  <Form.Select
                    value={formData.evaluation_type}
                    onChange={(e) => {
                      const newType = e.target.value;
                      setFormData({
                        ...formData,
                        evaluation_type: newType,
                        total_mark:
                          newType === 'grade' ? '0' : formData.total_mark,
                      });
                    }}
                    style={{
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0',
                      padding: '12px',
                    }}
                  >
                    <option value="mark">Mark Based</option>
                    <option value="grade">Grade Based</option>
                  </Form.Select>
                </Form.Group>
              </div>

              {formData.evaluation_type === 'mark' && (
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label
                      style={{
                        fontWeight: '600',
                        color: '#333',
                        marginBottom: '8px',
                      }}
                    >
                      Total Mark
                    </Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.total_mark}
                      onChange={(e) =>
                        setFormData({ ...formData, total_mark: e.target.value })
                      }
                      placeholder="Enter total marks"
                      style={{
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0',
                        padding: '12px',
                      }}
                    />
                  </Form.Group>
                </div>
              )}
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer
          style={{
            border: 'none',
            padding: '20px 30px',
            background: '#f8f9fa',
          }}
        >
          <Button
            variant="light"
            onClick={() => setShowModal(false)}
            style={{
              minWidth: '120px',
              padding: '10px 20px',
              borderRadius: '8px',
              fontWeight: '500',
              border: '1px solid #ddd',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              minWidth: '120px',
              padding: '10px 20px',
              borderRadius: '8px',
              fontWeight: '600',
              boxShadow: '0 4px 10px rgba(102, 126, 234, 0.3)',
            }}
          >
            {editData ? 'Update Test' : 'Create Test'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Compare Marks Modal */}
      <Modal
        show={showCompareModal}
        onHide={() => {
          setShowCompareModal(false);
          setCompareBatchId('');
          setSelectedTestIds([]);
        }}
        centered
        size="lg"
      >
        <Modal.Header
          closeButton
          style={{
            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            color: 'white',
            border: 'none',
            padding: '20px 25px',
          }}
        >
          <Modal.Title style={{ fontSize: '1.3rem', fontWeight: '600' }}>
            📊 Compare Student Marks
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '30px' }}>
          <div className="row g-3">
            <div className="col-md-4">
              <Form.Label
                style={{
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  color: '#495057',
                  marginBottom: '8px',
                  display: 'block',
                }}
              >
                Select Batch
              </Form.Label>
              <Form.Select
                value={compareBatchId}
                onChange={(e) => setCompareBatchId(e.target.value)}
                style={{
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0',
                  padding: '12px',
                }}
              >
                <option value="">Select Batch</option>
                {batches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.class_name} {b.division}
                  </option>
                ))}
              </Form.Select>
            </div>

            <div className="col-md-8">
              <Form.Label
                style={{
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  color: '#495057',
                  marginBottom: '8px',
                  display: 'block',
                }}
              >
                Select Tests (Hold Ctrl/Cmd for multiple)
              </Form.Label>
              <Form.Select
                multiple
                value={selectedTestIds}
                onChange={(e) =>
                  setSelectedTestIds(
                    Array.from(e.target.selectedOptions, (opt) => opt.value),
                  )
                }
                style={{
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0',
                  padding: '12px',
                  minHeight: '120px',
                }}
              >
                {availableTests.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.subject_name}) - {t.exam_date}
                  </option>
                ))}
              </Form.Select>
              {availableTests.length === 0 && compareBatchId && (
                <small
                  style={{
                    color: '#6c757d',
                    fontSize: '0.8rem',
                    marginTop: '5px',
                    display: 'block',
                  }}
                >
                  No tests available for this batch
                </small>
              )}
              {selectedTestIds.length > 0 && (
                <small
                  style={{
                    color: '#11998e',
                    fontSize: '0.8rem',
                    marginTop: '5px',
                    display: 'block',
                    fontWeight: '600',
                  }}
                >
                  ✓ {selectedTestIds.length} test(s) selected
                </small>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer
          style={{
            border: 'none',
            padding: '20px 30px',
            background: '#f8f9fa',
          }}
        >
          <Button
            variant="light"
            onClick={() => {
              setShowCompareModal(false);
              setCompareBatchId('');
              setSelectedTestIds([]);
            }}
            style={{
              minWidth: '120px',
              padding: '10px 20px',
              borderRadius: '8px',
              fontWeight: '500',
              border: '1px solid #ddd',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleCompareMarks();
              setShowCompareModal(false);
            }}
            disabled={selectedTestIds.length < 2}
            style={{
              background:
                selectedTestIds.length < 2
                  ? '#dee2e6'
                  : 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              border: 'none',
              minWidth: '120px',
              padding: '10px 20px',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: selectedTestIds.length < 2 ? 'not-allowed' : 'pointer',
              color: selectedTestIds.length < 2 ? '#6c757d' : 'white',
            }}
          >
            Compare Marks
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
};

export default TestsPage;
